import { sanitizeEmailHTML } from '../../services/sanitizeHTML';
import { EMAIL_CLICKED_LINK_CLASS, EMAIL_PREVIEW_BODY_CLASS } from './emailPreviewConstants';

export function extractHeadContent(rawHtml: string): string {
  let headContent = '';
  const headMatch = rawHtml.match(/<head[^>]*>([\s\S]*)<\/head>/i);
  if (!headMatch) {
    return headContent;
  }

  const headHtml = headMatch[1];
  const styleMatch = headHtml.match(/<style[^>]*>([\s\S]*?)<\/style>/gi);
  const metaMatch = headHtml.match(/<meta[^>]*>/gi);

  if (styleMatch) {
    headContent += styleMatch.join('\n');
  }
  if (metaMatch) {
    headContent += metaMatch.join('\n');
  }

  return headContent;
}

export function findLinkElement(doc: Document, highlightedLinkId: string): HTMLElement | null {
  const linkIdToFind = highlightedLinkId.startsWith('#')
    ? highlightedLinkId.slice(1)
    : highlightedLinkId;

  let link =
    doc.getElementById(linkIdToFind) ||
    (doc.querySelector(`a#${linkIdToFind}`) as HTMLElement | null);

  if (!link && highlightedLinkId.startsWith('#')) {
    link =
      doc.getElementById(highlightedLinkId) ||
      (doc.querySelector(`a[id="${highlightedLinkId}"]`) as HTMLElement | null);
  }

  if (!link) {
    link =
      doc.getElementById(highlightedLinkId) ||
      (doc.querySelector(`a#${highlightedLinkId}`) as HTMLElement | null) ||
      (doc.querySelector(`a[id="${highlightedLinkId}"]`) as HTMLElement | null);
  }

  return link;
}

export function highlightLinkInHtml(rawHtml: string, highlightedLinkId: string): string {
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(rawHtml, 'text/html');
    const link = findLinkElement(doc, highlightedLinkId);

    if (link) {
      link.classList.add(EMAIL_CLICKED_LINK_CLASS);
    }

    const bodyElement = doc.body || doc.documentElement;
    return bodyElement.innerHTML;
  } catch (error) {
    console.error('Failed to parse HTML for link highlighting:', error);
    return rawHtml;
  }
}

const CLICKED_LINK_STYLES = `
  <style>
    @font-face {
      font-family: 'Material Icons Outlined';
      font-style: normal;
      font-weight: 400;
      src: url(https://fonts.gstatic.com/s/materialiconsoutlined/v109/gok-H7zzDkdnRel8-DQ6KAXJ69wP1tGnf4ZGhUcel5euIg.woff2) format('woff2');
    }
    .material-icons {
      font-family: 'Material Icons Outlined';
      font-weight: normal;
      font-style: normal;
      font-size: 20px;
      line-height: 1;
      letter-spacing: normal;
      text-transform: none;
      display: inline-block;
      white-space: nowrap;
      word-wrap: normal;
      direction: ltr;
      -webkit-font-feature-settings: 'liga';
      -webkit-font-smoothing: antialiased;
    }
    .${EMAIL_PREVIEW_BODY_CLASS} {
      margin: 0;
      padding: 0;
      background-color: #ffffff;
      box-sizing: border-box;
      position: relative;
    }
    .${EMAIL_CLICKED_LINK_CLASS} {
      cursor: pointer;
    }
    [data-email-icon-target="true"]::before {
      content: "\\e913";
      font-family: 'Material Icons Outlined';
      position: absolute;
      left: -9999px;
      top: -9999px;
      transform: translate(-50%, -50%) rotate(45deg);
      font-size: 20pt;
      font-weight: 100;
      color: #ffffff;
      -webkit-text-stroke: 1px #143c34;
      text-shadow: none;
      opacity: 0;
      pointer-events: none;
      white-space: nowrap;
      z-index: 1000;
    }

  </style>`;

export function buildSanitizedEmailPreviewHtml(
  rawHtml: string,
  highlightedLinkId?: string | null,
): string {
  const headContent = extractHeadContent(rawHtml);

  let processedHtml = rawHtml;
  if (highlightedLinkId) {
    processedHtml = highlightLinkInHtml(rawHtml, highlightedLinkId);
  }

  const sanitizedBody = sanitizeEmailHTML(processedHtml);
  const clickedLinkStyles = highlightedLinkId ? CLICKED_LINK_STYLES : '';

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  ${headContent}${clickedLinkStyles}
</head>
<body style="margin: 0; padding: 0; background-color: #ffffff; box-sizing: border-box;">
<div class="${EMAIL_PREVIEW_BODY_CLASS}">
  ${sanitizedBody}
</div>
</body>
</html>`;
}
